import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { Button } from '../Button/Button';
import { Container } from '../Container/Container';
import { Text } from '../Text/Text';
import { ChatNavigatorProps } from './ChatNavigator.type';
import { useActiveNavMessage } from './useActiveNavMessage';
import { parseTagsFromContent } from '../../utils/cabTagSerializer';

// Sanitize a raw preview before rendering. The chat-index caps the preview at
// 120 code points server-side, which can slice a serialized tag token
// (`{{ticket:…|…}}`) mid-marker — drop any dangling `{{…` fragment (with an
// optional stray `}` and trailing ellipsis) the truncation left behind, and
// re-add the `...` hint in its place.
const cleanPreview = (raw: string): string => {
  const trimmed = raw.replace(/\{\{[^{}]*\}?\.*$/, '');
  return trimmed !== raw && !/\.\.\.$/.test(trimmed)
    ? `${trimmed.replace(/\s+$/, '')}...`
    : trimmed;
};

// Length budget for a preview row: keeps rows on one line instead of scrolling.
const PREVIEW_MAX_CHARS = 42;

// Flatten a preview to plain text: complete `{{…}}` tag tokens collapse to their
// human label (no chip), everything else stays as-is, then bound the width so the
// auto-width panel grows to fit the widest row on one line without scrolling.
const previewText = (raw: string): string => {
  const text = parseTagsFromContent(cleanPreview(raw))
    .segments.map((s) => s.content)
    .join('');
  return text.length > PREVIEW_MAX_CHARS ? `${text.slice(0, PREVIEW_MAX_CHARS)}…` : text;
};

// Vertical space one tick consumes: 2px bar + 8px gap
const TICK_PITCH = 10;
// Max turns shown in the rail window (matches the chat pagination limit)
const TICK_WINDOW_LIMIT = 20;
// Extra strips rendered beyond each window edge (at opacity 0) so entering /
// leaving strips slide+fade smoothly instead of popping in and out
const TICK_BUFFER = 2;

/**
 * ChatGPT-style conversation navigator. Collapsed: a compact, vertically
 * centered cluster of tick lines on the right edge showing a 1:1 sliding
 * window of the user turns around the active one — the blue tick shifts one
 * tick per message passed, and near the rail's edges the window slides along
 * instead of page-flipping. Hovering expands it into a rounded, scrollable
 * panel listing the first line of every user prompt. Always visible.
 */
export const ChatNavigator: React.FC<ChatNavigatorProps> = ({
  items,
  onSelect,
  containerRef,
  getMessageElement,
  loadedCount,
  oldestLoadedId,
  newestLoadedId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [maxTicks, setMaxTicks] = useState(40);

  // Tracked locally so scroll-driven updates re-render only this small
  // component, never the chat page with its hundreds of message subtrees.
  // setActiveId gives clicks a deterministic, instant highlight — no waiting
  // for the scroll to land and the detection line to notice.
  const [activeId, setActiveId] = useActiveNavMessage(
    containerRef,
    items,
    getMessageElement,
    loadedCount,
    oldestLoadedId,
    newestLoadedId,
  );

  const railRef = useRef<HTMLDivElement>(null);
  // Persistent start of the sliding tick window (index into `items`)
  const windowStartRef = useRef(0);

  // Sliding-window rail (ChatGPT-style): a 1:1 window of turns. The blue tick
  // walks freely inside the window — all the way to the edge strip — and only
  // scrolling beyond the edge slides the window one turn at a time. Strips are
  // keyed by turn id and positioned absolutely, so a slide animates every
  // strip gliding one pitch (with buffered strips fading in/out at the ends).
  const tickWindow = useMemo(() => {
    // Cap at the pagination limit: a fixed-size window keeps the cluster
    // compact and consistent, and makes each per-message shift clearly visible
    const size = Math.max(2, Math.min(maxTicks, TICK_WINDOW_LIMIT, items.length));
    const idx = activeId ? items.findIndex((i) => i.id === activeId) : -1;
    // Before anything is active (fresh thread at the bottom), light the last turn
    const activeIndex = idx === -1 ? items.length - 1 : idx;
    const maxStart = Math.max(0, items.length - size);

    let start = Math.min(Math.max(0, windowStartRef.current), maxStart);
    if (activeIndex < start) start = activeIndex;
    else if (activeIndex > start + size - 1) start = activeIndex - size + 1;
    start = Math.min(Math.max(0, start), maxStart);
    windowStartRef.current = start;

    const bufStart = Math.max(0, start - TICK_BUFFER);
    const bufEnd = Math.min(items.length, start + size + TICK_BUFFER);
    return {
      start,
      size,
      activeIndex,
      strips: items.slice(bufStart, bufEnd).map((item, i) => ({ item, index: bufStart + i })),
    };
  }, [items, maxTicks, activeId]);

  // Fit the tick cluster to the rail: fixed pitch per tick, so density adapts
  // to the available height instead of a hardcoded cap
  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const compute = () =>
      setMaxTicks(Math.max(2, Math.floor(el.clientHeight / TICK_PITCH)));
    compute();
    const resizeObserver = new ResizeObserver(compute);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, []);

  // When the panel opens, bring the active row into view (looked up via the
  // rail ref — the library Container doesn't forward refs)
  useEffect(() => {
    if (!expanded || !railRef.current) return;
    const el = railRef.current.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [expanded]);

  if (items.length < 2) return null;

  return (
    <div
      ref={railRef}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      className={clsx(
        'absolute right-[28px] top-[10px] bottom-[10px] w-[28px] z-20 transition-opacity duration-300',
        // Always visible; keep it fully opaque instead of fading in on chat hover/scroll.
        'opacity-100',
      )}
    >
      {expanded ? (
        /* Expanded: rounded scrollable panel listing every user prompt */
        <Container
          variant='primary'
          padding='md'
          className='absolute right-0 top-1/2 -translate-y-1/2 max-h-full w-max min-w-[260px] overflow-y-auto overflow-x-hidden modern-scrollbar-spaced border border-border-secondary shadow-bg-v2-md rounded-bg-v2-2xl cursor-default'
        >
          {items.map((item) => {
            const isActive = item.id === activeId;
            return (
              /* Wrapper carries the scroll anchor — Button doesn't forward data-* */
              <div key={item.id} data-active={isActive || undefined}>
                <Button
                  variant='ghost'
                  size='small'
                  onClick={() => {
                    setExpanded(false);
                    setActiveId(item.id);
                    onSelect(item.id, item.timestamp);
                  }}
                  className={clsx(
                    'w-full !justify-start !font-bg-v2-regular !rounded-bg-v2-md transition-colors duration-100',
                    isActive && '!bg-bg-secondary',
                  )}
                  label={
                    <Text as='span' variant='text-sm' color='primary' className='block w-full max-w-full whitespace-nowrap text-left'>
                      {item.preview ? previewText(item.preview) : '(empty message)'}
                    </Text>
                  }
                />
              </div>
            );
          })}
        </Container>
      ) : (
        /* Collapsed: compact centered cluster — strips keyed by turn id and
           absolutely positioned, so window slides animate as gliding motion */
        <div className='h-full flex flex-col items-end justify-center pointer-events-none'>
          <div
            className='relative w-bg-v2-xl'
            style={{ height: (tickWindow.size - 1) * TICK_PITCH + 3 }}
          >
            {tickWindow.strips.map(({ item, index }) => {
              const slot = index - tickWindow.start;
              const inWindow = slot >= 0 && slot < tickWindow.size;
              const isActive = index === tickWindow.activeIndex;
              return (
                <span
                  key={item.id}
                  className={clsx(
                    'absolute right-0 rounded-bg-v2-full w-bg-v2-xl',
                    // utility-brand-800 (not bg-brand-solid): the solid brand
                    // chartreuse only reaches 1.7:1 on the light-mode white
                    // chat background. This token flips per theme — a darker
                    // brand green on light, a lighter one on dark.
                    isActive ? 'h-[3px] bg-utility-brand-800' : 'h-bg-v2-xxs bg-fg-tertiary',
                  )}
                  style={{
                    top: slot * TICK_PITCH,
                    opacity: !inWindow ? 0 : isActive ? 1 : 0.6,
                    transition: 'top 150ms ease-out, opacity 150ms ease-out',
                  }}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
